import {
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import ShowLoader from "@/components/Loader";
import Toaster from "../../components/Toaster";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  doForgotPassword,
  resetStatus,
} from "../../store/slices/forgotPasswordSlice";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { Header } from ".";
import { useTranslation } from "react-i18next";

export function ForgotPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation("forgotPassword");

  // Dynamic Yup validation messages
  const schema = yup.object({
    email: yup
      .string()
      .email(t("validation.invalidEmail"))
      .required(t("validation.emailRequired")),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
    },
  });

  const { loading } = useSelector((state) => state.forgotPassword);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(doForgotPassword(data)).unwrap();

      if (result.status) {
        Toaster.success(
          result.message || t("messages.resetLinkSent")
        );

        dispatch(resetStatus());
        reset();
      } else {
        Toaster.error(
          result.message || t("messages.somethingWentWrong")
        );

        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(
        err?.message || t("messages.forgotPasswordFailed")
      );

      dispatch(resetStatus());
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {loading && <ShowLoader />}

      <ScrollToTop />

      <Header />

      <main
        className="relative flex-grow w-full flex items-center justify-center p-4"
        style={{
          backgroundImage: "url('/img/bgImg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-black opacity-40 z-0"></div>

        {/* Forgot Password Card */}
        <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-xl p-8">

          {/* Title */}
          <Typography
            variant="h4"
            color="blue-gray"
            className="mb-6 text-left font-bold"
          >
            {t("title")}
          </Typography>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >

            {/* Email */}
            <div className="flex flex-col gap-2">
              <Typography
                variant="h6"
                color="blue-gray"
                className="-mb-1"
              >
                {t("email")}
              </Typography>

              <Input
                type="email"
                size="lg"
                placeholder={t("emailPlaceholder")}
                {...register("email")}
                error={Boolean(errors?.email)}
                className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                labelProps={{
                  className: "before:content-none after:content-none",
                }}
              />

              {/* Yup Error */}
              {errors?.email && (
                <p className="text-red-500 text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              className="btn-material bg-black hover:bg-gray-800 text-base py-3"
            >
              {t("sendResetLink")}
            </Button>

            {/* Back To Sign In */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-2 px-4 md:px-6">
              <div
                className="flex items-center gap-2 text-gray-700 cursor-pointer hover:text-gray-900 transition"
                onClick={() => navigate("/sign-in")}
              >
                <ArrowLeftIcon className="h-4 w-4" />

                <span className="font-medium">
                  {t("backToSignIn")}
                </span>
              </div>
            </div>
          </form>
        </div>

        {/* Copyright */}
        <Typography
          variant="small"
          className="absolute bottom-1 mt-0 left-0 right-0 text-center text-white/80 z-10"
        >
          {t("copyright")}
        </Typography>
      </main>
    </div>
  );
}

export default ForgotPassword;